import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Layout, message, Tooltip, Modal, Button, Table, Spin } from 'antd';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';
import { getUserProfile } from '../../Services/graphService';
import {
  SettingOutlined,
  UserOutlined,
  AppstoreOutlined,
} from '@ant-design/icons';
import { ActionCard } from '../Atoms/ActionCard';
import Communique from '../../assets/images/Communique.svg';
import ManagerAcr from '../../assets/images/ManagerAct.svg';
import LeaderBoard from '../../assets/images/LeaderBoard.svg';
import Administrator from '../../assets/images/Administrator.svg';
import AppreciateSVG from '../../assets/images/Appreciate.svg';
import CelebrateSVG from '../../assets/images/Celebrate.svg';
import MomentsSVG from '../../assets/images/Moments.svg';
import FeedSVG from '../../assets/images/Feed.svg';
import RewardsSVG from '../../assets/images/Rewards.svg';
import Rewards_PointSVG from '../../assets/images/Rewards_Point.svg';
import Wall_of_HonourSVG from '../../assets/images/Wall_of_honour.svg';
import Celebrate from './Celebrate';
import MyPoints from './MyPoints';
import Appreciate from './Appreciate';
import Rewards from './Rewards';
import axios from 'axios';
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import OpenSpecificUsersModal from './openSpecificUsersModal';

const { Sider, Content } = Layout;

export const AppLayoutContent = React.memo(() => {
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();
  const [userRoles, setUserRoles] = useState<string[]>(['employee']);
  const [rolesLoaded, setRolesLoaded] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [userName, setUserName] = useState('');
  const [isPointsModalVisible, setIsPointsModalVisible] = useState(false);
  const [isAppreciateModalVisible, setIsAppreciateModalVisible] = useState(false);
  const [isCelebrateModalVisible, setIsCelebrateModalVisible] = useState(false);
  const [isRewaredsModalVisible, setIsRewaredsModalVisible] = useState(false);
  const [employeeId, setEmployeeId] = useState(() => {
    // Load employeeId from localStorage on mount
    return localStorage.getItem('UserId') || '';
  });
  const [momentsVisible, setMomentsVisible] = useState(false);
  const [predefinedRoles, setPredefinedRoles] = useState(['employee', 'people Manager', 'admin', 'unithead', 'ELT']);
  const [isApprovalModalVisible, setIsApprovalModalVisible] = useState(false);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedNominationId, setSelectedNominationId] = useState<string | null>(null);
  const [hasCheckedApprovals, setHasCheckedApprovals] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const { t } = useTranslation();

  // Memoize API URL to prevent recreation
  const API_URL = useMemo(() => process.env.REACT_APP_API_URL, []);
  
  // Track authentication state to prevent concurrent requests
  const authStateRef = useRef({
    isTokenRequestInProgress: false,
    tokenCache: null as string | null,
    tokenExpiry: 0
  });

  interface AnniversaryData {
    peerAnniversaries: any; // Replace `any` with the actual type if known
    selfAnniversary: any;   // Replace `any` with the actual type if known
  }

  const [anniversaryData, setAnniversaryData] = useState<AnniversaryData | null>(null);

  const handleOpenSpecificUsersModal = (nominationId: string) => {
    setSelectedNominationId(nominationId);
  };

  const handleCloseSpecificUsersModal = () => {
    setSelectedNominationId(null);
  };
  // const predefinedRoles = ['Employee', 'People Manager', 'Admin', 'Unit Head'];
  const fetchToken = useCallback(async () => {
    try {
      // Check if there's already a token request in progress
      if (authStateRef.current.isTokenRequestInProgress) {
        // Wait for the current request to complete
        return new Promise((resolve, reject) => {
          const checkInterval = setInterval(() => {
            if (!authStateRef.current.isTokenRequestInProgress) {
              clearInterval(checkInterval);
              if (authStateRef.current.tokenCache) {
                resolve(authStateRef.current.tokenCache);
              } else {
                reject(new Error('Token request failed'));
              }
            }
          }, 100);
          
          // Timeout after 10 seconds
          setTimeout(() => {
            clearInterval(checkInterval);
            reject(new Error('Token request timeout'));
          }, 10000);
        });
      }
      
      // Check cached token first
      const now = Date.now();
      if (authStateRef.current.tokenCache && authStateRef.current.tokenExpiry > now) {
        return authStateRef.current.tokenCache;
      }
      
      if (!accounts[0]) throw new Error('No account found');
      
      // Set flag to prevent concurrent requests
      authStateRef.current.isTokenRequestInProgress = true;
      
      try {
        const res = await instance.acquireTokenSilent({
          ...loginRequest,
          account: accounts[0]
        });
        
        // Cache the token with expiry
        authStateRef.current.tokenCache = res.idToken;
        authStateRef.current.tokenExpiry = now + (50 * 60 * 1000); // 50 minutes
        
        return res.idToken;
      } finally {
        authStateRef.current.isTokenRequestInProgress = false;
      }
    } catch (error) {
      authStateRef.current.isTokenRequestInProgress = false;
      console.error('Token fetch failed:', error);
      
      // If it's an interaction required error, don't throw immediately
      if (error instanceof Error && error.message.includes('interaction_in_progress')) {
        // Wait a bit and retry once
        await new Promise(resolve => setTimeout(resolve, 2000));
        try {
          const res = await instance.acquireTokenSilent({
            ...loginRequest,
            account: accounts[0]
          });
          return res.idToken;
        } catch (retryError) {
          throw retryError;
        }
      }
      
      throw error;
    }
  }, [instance, accounts]);

  // Removed callMsGraph - now using centralized service

  const getCustomRoles = useCallback(async (id: string) => {
    if (!id) return;
    
    try {
      const token = await fetchToken();
      const response = await axios.get(`${API_URL}/api/Roles/ExistingUserRoles`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 30000
      });

      if (response.status === 200 && response.data) {
        const customRoles = response.data.map((role: { customRoleName: string }) => 
          role.customRoleName.replace(/\s+/g, '').trim().toLowerCase()
        );
        
        setPredefinedRoles(customRoles);
      }
    } catch (error) {
      console.error('Error fetching custom roles:', error);
    }
  }, [fetchToken, API_URL]);

  // getCustomRoles now called inside fetchEmployeeId via Promise.all — removed standalone effect

  const requestProfileData = useCallback(async () => {
    const startTime = performance.now();
    try {
      setIsInitialLoading(true);
      setProfileError(null);
      setIsAuthenticating(true);
      
      if (!accounts[0]) throw new Error('No account found');
      
      // Check if another authentication is in progress
      if (authStateRef.current.isTokenRequestInProgress) {
        console.log('[Profile] Authentication already in progress, waiting...');
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
      
      const graphStartTime = performance.now();
      const profile = await getUserProfile(instance, 'https://login.microsoftonline.com/560ec2b0-df0c-4e8c-9848-a15718863bb6', true);
      console.log(`[Profile] Graph API call completed in ${(performance.now() - graphStartTime).toFixed(2)}ms`);
      
      setUserName(profile.displayName || 'Default Nominator');
      setUserEmail(profile.mail?.toLowerCase() || '');
      localStorage.setItem("UserMail", profile.mail?.toLowerCase() || '');
      localStorage.setItem("UserId", profile.id || '');
      //localStorage.setItem("UserId", 'c29c6934-edc3-40c7-ab77-456d6054f8b7'); // Temporary hardcoded ID for testing
    } catch (error) {
      console.error(`[Profile] Profile data request failed after ${(performance.now() - startTime).toFixed(2)}ms:`, error);
      setProfileError('Failed to load profile data');
      
      // Handle interaction_in_progress error specifically
      if (error instanceof Error && error.message.includes('interaction_in_progress')) {
        console.log('[Profile] Authentication interaction in progress');
        setIsAuthenticating(false);
        return;
      }
      
      if (!(error instanceof Error) || !error.message.includes('interaction_in_progress')) {
        // Only redirect if it's not an interaction error
        instance.loginRedirect(loginRequest);
      }
    } finally {
      setIsInitialLoading(false);
      setIsAuthenticating(false);
    }
  }, [instance, accounts]);

  // Direct version used in parallel fetch — accepts userId param instead of relying on state
  const checkUserRoleForAnniversary_direct = useCallback(async (userId: string) => {
    if (!userId) return;
    try {
      const token = await fetchToken();
      const response = await axios.get(`${API_URL}/api/WorkAnniversaryMilestoneService/CheckUserRoleForAnniversary/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 30000
      });
      if (response.status === 200 && response.data) {
        const { roles, peerAnniversaries, selfAnniversary } = response.data;
        const visible = roles.includes('Peer') || roles.includes('Self');
        setMomentsVisible(visible);
        setAnniversaryData(visible ? { peerAnniversaries, selfAnniversary } : null);
      }
    } catch (error) {
      console.error('Error checking user role for anniversary:', error);
    }
  }, [fetchToken, API_URL]);

  // Direct version used in parallel fetch
  const checkPendingApprovals_direct = useCallback(async (userId: string) => {
    if (!userId || hasCheckedApprovals) return;
    try {
      const token = await fetchToken();
      const response = await axios.get(`${API_URL}/api/RewardNomination/getPendingVisibilityApprovals/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 30000
      });
      if (response.status === 200 && response.data.length > 0) {
        setPendingApprovals(response.data);
        setIsApprovalModalVisible(true);
      }
      setHasCheckedApprovals(true);
    } catch (error) {
      console.error('Error fetching pending approvals:', error);
      setHasCheckedApprovals(true);
    }
  }, [fetchToken, API_URL, hasCheckedApprovals]);

  const checkUserRoleForAnniversary = useCallback(async () => {
    if (!employeeId) return;

    try {
      const token = await fetchToken();
      const response = await axios.get(`${API_URL}/api/WorkAnniversaryMilestoneService/CheckUserRoleForAnniversary/${employeeId}`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 30000
      });

      if (response.status === 200) {
        if (!response.data) {
          setMomentsVisible(false);
          return;
        }

        const { roles, peerAnniversaries, selfAnniversary } = response.data;
        const visible = roles.includes('Peer') || roles.includes('Self');
        const anniversaryData = { peerAnniversaries, selfAnniversary };

        setMomentsVisible(visible);
        setAnniversaryData(visible ? anniversaryData : null);
      }
    } catch (error) {
      console.error('Error checking user role for anniversary:', error);
    }
  }, [employeeId, fetchToken, API_URL]);

  // checkUserRoleForAnniversary now called inside fetchEmployeeId via Promise.all

  const fetchEmployeeId = useCallback(async () => {
    if (!userEmail) return;
    
    try {
      const token = await fetchToken();
      const response = await axios.get(`${API_URL}/api/Users/CheckEmail`, {
        params: { email: userEmail },
        headers: { Authorization: `Bearer ${token}` },
        timeout: 30000
      });
      
      if (response.status === 200 && response.data.userId) {
        const userId = response.data.userId;

        setEmployeeId(userId);
        localStorage.setItem('UserId', userId);

        // Run all post-login lookups in parallel — no sequential waiting
        await Promise.all([
          checkIfPeopleManager(userId),
          fetchUserRoles(userId),
          checkUserRoleForAnniversary_direct(userId),
          checkPendingApprovals_direct(userId),
          getCustomRoles(userId),
        ]);
      } else {
        // User not found in DB — still dismiss the spinner
        setRolesLoaded(true);
      }
    } catch (error) {
      console.error('Error fetching employee ID:', error);
      // Ensure spinner is dismissed even if the request fails
      setRolesLoaded(true);
    }
  }, [userEmail, fetchToken, API_URL]);


  const checkIfPeopleManager = useCallback(async (id: string) => {
    if (!id) return;
    
    try {
      const token = await fetchToken();
      const response = await axios.get(`${API_URL}/api/Users/IsPeopleManager/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 30000
      });

      const isPeopleManager = response.status === 200 && response.data;
      
      if (isPeopleManager) {
        setUserRoles(prevRoles => Array.from(new Set([...prevRoles, 'peoplemanager'])));
      }
    } catch (error) {
      console.error('Error checking if user is a people manager:', error);
    }
  }, [fetchToken, API_URL]);
  const fetchUserRoles = useCallback(async (id: string) => {
    if (!id) return;
    
    try {
      const token = await fetchToken();
      const response = await axios.get(`${API_URL}/api/Roles/CheckUserRoles/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 30000
      });
      
      if (response.status === 200 && Array.isArray(response.data)) {
        const apiRoles = response.data.map((role: string) => role.replace(/\s+/g, '').trim().toLowerCase());
        const matchedRoles = predefinedRoles.filter(role => apiRoles.includes(role.toLowerCase()));
        // Include all API roles (e.g. special-PatOnTheBack) not just predefined ones
        const allRoles = Array.from(new Set([...matchedRoles, ...apiRoles]));

        if (userRoles.includes("peoplemanager")) {
          allRoles.push("peoplemanager");
        }

        setUserRoles(prevRoles => {
          return Array.from(new Set([...prevRoles, ...allRoles]));
        });
        setRolesLoaded(true);
      }
    } catch (error) {
      console.error('Error fetching user roles:', error);
      setRolesLoaded(true);
    }
  }, [fetchToken, API_URL, predefinedRoles, userRoles]);
  // fetchUserRoles now called inside fetchEmployeeId via Promise.all — removed duplicate effect

  useEffect(() => {
    requestProfileData();
  }, [requestProfileData]);

  // On mount: fast-path role load using cached internal DB userId.
  // If no cached userId or MSAL not ready yet, skip — fetchEmployeeId handles it.
  useEffect(() => {
    const storedUserId = localStorage.getItem('UserId');
    if (!storedUserId || !accounts[0]) {
      // Don't setRolesLoaded here — fetchEmployeeId will set it once roles actually load
      return;
    }
    Promise.all([
      checkIfPeopleManager(storedUserId),
      fetchUserRoles(storedUserId),
      checkUserRoleForAnniversary_direct(storedUserId),
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  useEffect(() => {
    if (userEmail && !isAuthenticating) {
      fetchEmployeeId();
    }
  }, [userEmail, fetchEmployeeId, isAuthenticating]);

  useEffect(() => {
    const checkPendingApprovals = async () => {
      // Only check once per session
      if (hasCheckedApprovals) return;
      
      try {
        setLoading(true);
        const token = await fetchToken();
        const baseURL = process.env.REACT_APP_API_URL;
        const response = await axios.get(`${baseURL}/api/RewardNomination/getPendingVisibilityApprovals/${employeeId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.status === 200 && response.data.length > 0) {
          setPendingApprovals(response.data);
          setIsApprovalModalVisible(true); // Automatically open the modal if data is found
        }
        setHasCheckedApprovals(true); // Mark as checked to prevent re-running
      } catch (error) {
        console.error('Error fetching pending approvals:', error);
        setHasCheckedApprovals(true); // Mark as checked even on error
      } finally {
        setLoading(false);
      }
    };

    // pendingApprovals now checked inside fetchEmployeeId via Promise.all
  }, []);

  const widgets = [
    { icon: <AppstoreOutlined style={{ fontSize: '24px', color: '#1F2937' }} />, tooltip: 'Show More' },
    { icon: <SettingOutlined style={{ fontSize: '24px', color: '#1F2937' }} />, tooltip: 'Settings' },
    { icon: <UserOutlined style={{ fontSize: '24px', color: '#1F2937' }} />, tooltip: 'Profile' },
  ];

  // Add a button to open the modal
  const handleOpenApprovalModal = () => {
    setIsApprovalModalVisible(true);
  };

  // Memoized click handlers to prevent recreation on every render
  const handleCelebrateClick = useCallback(() => {
    setIsCelebrateModalVisible(true);
  }, []);

  const handleCelebrateModalClose = useCallback(() => {
    setIsCelebrateModalVisible(false);
  }, []);

  const handleAppreciateClick = useCallback(() => {
    // Clear any cached eCard data to force fresh load from database
    const ecardCacheKeys = Object.keys(localStorage).filter(key => key.includes('ecard') || key.includes('Ecard'));
    ecardCacheKeys.forEach(key => localStorage.removeItem(key));
    setIsAppreciateModalVisible(true);
  }, []);

  const handleAppreciateModalClose = useCallback(() => {
    setIsAppreciateModalVisible(false);
  }, []);

  const handleRewardsClick = useCallback(() => {
    setIsRewaredsModalVisible(true);
  }, []);

  const handleRewardsModalClose = useCallback(() => {
    setIsRewaredsModalVisible(false);
  }, []);

  const handleManagerActionsClick = useCallback(() => {
    navigate('/manager-actions');
  }, [navigate]);

  const handleAdministratorClick = useCallback(() => {
    navigate('/admindashboard');
  }, [navigate]);

  const handleAceAwardsClick = useCallback(() => {
    navigate('/review/level2/individual');
  }, [navigate]);

  const handleFeedsClick = useCallback(() => {
    navigate('/feeds');
  }, [navigate]);
  
  const handleMomentsClick = useCallback(() => {
    navigate('/moments', { state: { anniversaryData } });
  }, [navigate, anniversaryData]);
  
  const handleWallOfFameClick = useCallback(() => {
    navigate('/WallOfFame');
  }, [navigate]);
  
  const handleLeaderBoardClick = useCallback(() => {
    navigate('/leaderBoard');
  }, [navigate]);

  const handlePointsClick = useCallback(() => {
    setIsPointsModalVisible(true);
  }, []);

  const handleCommuniqueClick = useCallback(() => {
    navigate('/ApteanCommunique');
  }, [navigate]);

  const handlePointsModalClose = useCallback(() => {
    setIsPointsModalVisible(false);
  }, []);

  // Function to update visibility status
  interface UpdateVisibilityStatusPayload {
    nominationIds: string[];
    visibilityStatus: boolean;
  }

  // const updateVisibilityStatus = async (
  //   nominationIds: UpdateVisibilityStatusPayload['nominationIds'],
  //   visibilityStatus: UpdateVisibilityStatusPayload['visibilityStatus']
  // ): Promise<void> => {
  //   try {
  //     const token: string = await fetchToken();
  //     const baseURL: string | undefined = process.env.REACT_APP_API_URL;
  //     if (!baseURL) {
  //       throw new Error('API base URL is not defined');
  //     }

  //     const response = await axios.post(
  //       `${baseURL}/api/RewardNomination/updateVisibilityStatus`,
  //       { nominationIds, visibilityStatus },
  //       { headers: { Authorization: `Bearer ${token}` } }
  //     );

  //     if (response.status === 200) {
  //       message.success('Visibility status updated successfully.');
  //     }
  //   } catch (error) {
  //     console.error('Error updating visibility status:', error);
  //     message.error('Failed to update visibility status.');
  //   }
  // };

  const updateVisibilityStatus = useCallback(async (
    nominationIds: string[],
    visibilityStatus: string,
    visibilityTo?: string[] // Optional parameter for specific users
  ): Promise<void> => {
    try {
      const token = await fetchToken();
      if (!API_URL) {
        throw new Error('API base URL is not defined');
      }

      const response = await axios.post(
        `${API_URL}/api/RewardNomination/updateVisibilityStatus`,
        { nominationIds, visibilityStatus, visibilityTo },
        { 
          headers: { Authorization: `Bearer ${token}` },
          timeout: 30000
        }
      );

      if (response.status === 200) {
        message.success('Visibility status updated successfully.');
      }
    } catch (error) {
      console.error('Error updating visibility status:', error);
      message.error('Failed to update visibility status.');
    }
  }, [fetchToken, API_URL]);


  const ApprovalModal = () => {
    const handleApprove = async (nominationId: string): Promise<void> => {
      try {
        await updateVisibilityStatus([nominationId], "public");
        message.success('Nomination approved successfully.');
        setIsApprovalModalVisible(false); // Close the modal
      } catch (error) {
        console.error('Error approving nomination:', error);
        message.error('Failed to approve nomination.');
      }
    };

    const handleReject = async (nominationId: string): Promise<void> => {
      try {
        await updateVisibilityStatus([nominationId], "private");
        message.success('Nomination rejected successfully.');
        setIsApprovalModalVisible(false); // Close the modal
      } catch (error) {
        console.error('Error rejecting nomination:', error);
        message.error('Failed to reject nomination.');
      }
    };

    interface PendingApproval {
      nominationId: string;
      citation: string;
      nominationDate: string;
      rewardName: string; // Added rewardName property
    }

    const columns: Array<{
      title: string;
      dataIndex?: keyof PendingApproval;
      key: string;
      render?: (_: unknown, record: PendingApproval) => JSX.Element;
    }> = [
        {
          title: 'Reward Name',
          dataIndex: 'rewardName',
          key: 'rewardName',
        },
        {
          title: 'Citation',
          dataIndex: 'citation',
          key: 'citation',
          render: (_: unknown, record: PendingApproval) => {
            const extractCitation = (description: string): string => {
              const citationRegex = /~citation~([\s\S]*?)~FormFields~|~citation~([\s\S]*)/;
              const match = description.match(citationRegex);
              return match ? (match[1] || match[2] || '').trim() : description;
            };
            return <span>{extractCitation(record.citation)}</span>;
          },
        },
        {
          title: 'Nomination Date',
          dataIndex: 'nominationDate',
          key: 'nominationDate',
        },
        {
          title: 'Actions',
          key: 'actions',
          render: (_, record) => (
            <div>
              <Button
                type="primary"
                onClick={() => handleApprove(record.nominationId)}
                style={{ marginRight: 8, backgroundColor: 'rgb(69 221 69)' }}
              >
                Make it public
              </Button>
              <Button danger
                style={{ marginRight: 8 }}
                onClick={() => handleReject(record.nominationId)}>
                Keep it private
              </Button>
              <Button
                style={{ marginRight: 8 }}
                onClick={() => handleOpenSpecificUsersModal(record.nominationId)}
              >
                Share with Specific Users
              </Button>
            </div>
          ),
        },
      ];

    // return (
    //   <Modal
    //     visible={isApprovalModalVisible}
    //     onCancel={() => setIsApprovalModalVisible(false)}
    //     footer={null}
    //     width={900}
    //     centered
    //     bodyStyle={{
    //       minHeight: '320px',
    //       maxHeight: '330px', // Set a maximum height for the modal body
    //       borderRadius: '8px',
    //       padding: '20px',
    //       overflow: 'auto', // Enable scrolling when content exceeds the height
    //       position: 'relative',
    //     }}
    //     title={
    //       <div
    //         style={{
    //           display: 'flex',
    //           alignItems: 'center',
    //           justifyContent: 'center',
    //           backgroundColor: 'linear-gradient(90deg, rgb(134, 41, 164) 0%, rgb(230, 156, 255) 100%);',
    //           color: '#fff',
    //           padding: '8px 10px',
    //           borderRadius: '8px 8px 0 0',
    //         }}
    //       >
    //         <span style={{ fontSize: '16px', fontWeight: 'bold', textAlign: 'center', }}>Share the Good News!</span>
    //       </div>
    //     }
    //   >
    //     <style>{`
    //     .ant-modal .ant-modal-content {
    //       padding: 0 !important; 
    //     }

    //     .ant-modal .ant-modal-header {
    //       background: linear-gradient(90deg, rgb(134, 41, 164) 0%, rgb(230, 156, 255) 100%);
    //       color: #fff;
    //       border-radius: 8px 8px 0 0;
    //     }
    //       .ant-modal-footer{
    //       padding: 20px !important;
    //       }
    //       .ant-modal-close{
    //       top:4px !important;
    //       margon-top: 0px !important;
    //       }
    //       .ant-modal-confirm-body-wrapper{
    //       padding: 20px !important;}
    //   `}
    //     </style>
    //     <Table
    //       dataSource={pendingApprovals}
    //       columns={columns}
    //       rowKey="nominationId"
    //       loading={loading}
    //     />
    //     {selectedNominationId && (
    //       <OpenSpecificUsersModal
    //         nominationId={selectedNominationId}
    //         onClose={handleCloseSpecificUsersModal}
    //         onAfterShare={() => setIsApprovalModalVisible(false)} // <-- Add this line
    //       />
    //     )}
    //   </Modal>
    // );
  };

  // Memoize topRowCards to prevent recreation on every render
  const topRowCards = useMemo(() => [
    {
      icon: <div className="w-20 h-20 md:w-24 md:h-24 flex items-center justify-center">
        <img src={FeedSVG} alt="Feeds" className="w-14 h-8 object-contain" />
      </div>,
      title: t('my_feeds'),
      onClick: handleFeedsClick,
      message: t('stay_connected'),
      background: 'linear-gradient(238.85deg, #FFDE58 0%, #EDBB47 100%)',
      border: '1px solid #FBBF24',
      rolesAllowed: ['employee', 'peoplemanager', 'admin']
    },
    {
      icon:
        <div className="w-20 h-20 md:w-24 md:h-24 flex items-center justify-center">
          <img src={MomentsSVG} alt="Feeds" className="w-14 h-8 object-contain" />
        </div>,
      title: t('moments'),
      onClick: handleMomentsClick,
      message: t('moments_milestone'),
      background: 'linear-gradient(238.85deg, #F3F4F6 0%, #9CA3AF 100%)',
      border: '1px solid #9CA3AF',
      rolesAllowed: ['employee', 'admin'],
      visible: momentsVisible, // Add visibility condition
    },
    {
      icon: <div className="w-20 h-20 md:w-24 md:h-24 flex items-center justify-center">
        <img src={AppreciateSVG} alt="Appreciate" className="w-8 h-8 object-contain" />
      </div>,
      title: t('appreciate'),
      message: t('show_gratitude'),
      onClick: handleAppreciateClick,
      background: 'linear-gradient(238.85deg, #E69CFF 0%, #8629A4 100%)',
      border: '1px solid #8629A4',
      rolesAllowed: ['employee', 'peoplemanager', 'admin']
    },
    {
      icon: <div className="w-20 h-20 md:w-24 md:h-24 flex items-center justify-center">
        <img src={CelebrateSVG} alt="Celebrate" className="w-8 h-8 object-contain" />
      </div>,
      title: t('celebrate'),
      message: t('celebrating_occasion'),
      onClick: handleCelebrateClick,
      background: 'linear-gradient(239.03deg, #E6FFCC 0%, #69C3A2 97.46%)',
      border: '1px solid #69C3A2',
      rolesAllowed: ['employee', 'peoplemanager', 'admin']
    },
    {
      icon: <div className="w-20 h-20 md:w-24 md:h-24 flex items-center justify-center">
        <img src={Rewards_PointSVG} alt="Rewards" className="w-8 h-8 object-contain" />
      </div>,
      title: t('rewards'),
      onClick: handleRewardsClick,
      message: t('lets_nominate'),
      background: 'linear-gradient(238.85deg, #DCFCE7 0%, #4ADE80 100%)',
      border: '1px solid #4ADE80',
      rolesAllowed: ['peoplemanager', "unithead", "admin", 'employee']
    },
    // {
    //   icon: <div className="w-20 h-20 md:w-24 md:h-24 flex items-center justify-center">
    //     <img src={RewardsSVG} alt="Rewards Points" className="w-8 h-8 object-contain" />
    //   </div>,
    //   title: 'My Points',
    //   message:'Coming Soon...',
    //   comingSoon: true,      
    //   background: 'linear-gradient(238.85deg, #D1FAE5 0%, #34D399 100%)',
    //   border: '1px solid #34D399',
    //   rolesAllowed:['Employee']
    // },
    {
      icon: <div className="w-20 h-20 md:w-24 md:h-24 flex items-center justify-center">
        <img src={RewardsSVG} alt="Rewards Points" className="w-8 h-8 object-contain" />
      </div>,
      title: t('my_points'),
      onClick: handlePointsClick,
      message: t('reedem_your_points'),
      background: 'linear-gradient(239.03deg, #FFCCE8 0%, #8669C3 97.46%)',
      border: '1px solid #8669C3',
      rolesAllowed: ['employee']
    },
    // {
    //   icon: <div className="w-20 h-20 md:w-24 md:h-24 flex items-center justify-center">
    //     <img src={Wall_of_HonourSVG} alt="Wall of Honour" className="w-8 h-8 object-contain" />
    //   </div>,
    //   title: 'Wall of Fame',
    //   message:'Coming Soon...',
    //   comingSoon: true,
    //   background: 'linear-gradient(238.85deg, #F3F4F6 0%, #9CA3AF 100%)',
    //   border: '1px solid #9CA3AF',
    //   rolesAllowed:['Employee','Admin']
    // },

    {
      icon: <div className="w-20 h-20 md:w-24 md:h-24 flex items-center justify-center">
        <img src={Communique} alt="Communique" className="w-8 h-8 object-contain" />
      </div>,
      title: t('communique'),
      message: t('COE_communique'),
      onClick: handleCommuniqueClick,
      //comingSoon: true,
      background: 'linear-gradient(238.85deg, #F4F4F4 0%, #E5DFE7 100%)',
      border: '1px solid rgb(199, 204, 214)',
      rolesAllowed: ['admin', 'employee']
    },

    //Phase 2 Will be implemented in future
    // {
    //   icon: <div className="w-20 h-20 md:w-24 md:h-24 flex items-center justify-center">
    //     <img src={Communique} alt="Rewards Points" className="w-8 h-8 object-contain" />
    //   </div>,
    //   title: 'Communique',
    //   message:'Announcements!',      
    //   background: 'linear-gradient(238.85deg, #F3F4F6 0%,rgb(199, 204, 214) 100%)',
    //   border: '1px solid rgb(199, 204, 214)',
    //   rolesAllowed:['Admin']
    // },
    {
      icon: <div className="w-20 h-20 md:w-24 md:h-24 flex items-center justify-center">
        <img src={LeaderBoard} alt="Wall of Honour" className="w-8 h-8 object-contain" />,
      </div>,
      title: t('leader_board'),
      onClick: handleLeaderBoardClick,
      message: t('reward_toppers!'),
      background: 'linear-gradient(238.85deg, #F3E8FF 0%, #E879F9 100%)',
      border: '1px solid #E879F9',
      rolesAllowed: ['admin', 'employee']
    },

    {
      icon: <div className="w-20 h-20 md:w-24 md:h-24 flex items-center justify-center">
        <img src={Wall_of_HonourSVG} alt="Wall of Honour" className="w-8 h-8 object-contain" />
      </div>,
      title: t('walloffame'),
      onClick: handleWallOfFameClick,
      message: t('aptean_stars'),
      background: 'linear-gradient(239.03deg, #E6FFCC 0%, #69C3A2 97.46%)',
      border: '1px solid #69C3A2',
      rolesAllowed: ['employee', 'admin']
    },
    // {
    //   icon: <div className="w-20 h-20 md:w-24 md:h-24 flex items-center justify-center">
    //     <img src={LeaderBoard} alt="Leader Board" className="w-8 h-8 object-contain" />
    //   </div>,
    //   title: 'Leader Board',
    //   message: 'Coming Soon...',
    //   comingSoon: true,
    //   background: 'linear-gradient(238.85deg, #F3E8FF 0%, #E879F9 100%)',
    //   border: '1px solid #E879F9',
    //   rolesAllowed:['Admin','Employee']
    // },
    {
      icon: <div className="w-20 h-20 md:w-24 md:h-24 flex items-center justify-center">
        <img src={ManagerAcr} alt="Manager Actions" className="w-8 h-8 object-contain" />
      </div>,
      title: t('manager_actions'),
      onClick: handleManagerActionsClick,
      message: t('empowered_teamns'),
      background: 'linear-gradient(240.02deg, #AAE7F2 0%, #68C4D8 98.34%)',
      border: '1px solid #68C4D8',
      rolesAllowed: ["unithead"]

    },
    {
      icon: <div className="w-20 h-20 md:w-24 md:h-24 flex items-center justify-center">
        <img src={Administrator} alt="Administrator" className="w-8 h-8 object-contain" />
      </div>,
      title: t('administrator'),
      onClick: handleAdministratorClick,
      message: t('customize_preferences'),
      background: 'linear-gradient(238.85deg, #F3F4F6 0%, #9CA3AF 100%)',
      border: '1px solid #9CA3AF',
      rolesAllowed: ['admin']
    },
    {
      icon: <div className="w-20 h-20 md:w-24 md:h-24 flex items-center justify-center">
        <img src={Administrator} alt="Administrator" className="w-8 h-8 object-contain" />
      </div>,
      title: 'Ace Awards',
      onClick: handleAceAwardsClick,
      message: 'Approve Ace Nomination',
      background: 'linear-gradient(238.85deg, #F3F4F6 0%, #9CA3AF 100%)',
      border: '1px solid #9CA3AF',
      rolesAllowed: ['elt']
    },
  ], [
    t, handleFeedsClick, handleMomentsClick, handleAppreciateClick, handleCelebrateClick,
    handleRewardsClick, handlePointsClick, handleCommuniqueClick, handleLeaderBoardClick,
    handleWallOfFameClick, handleManagerActionsClick, handleAdministratorClick,
    handleAceAwardsClick, momentsVisible
  ]);

  // const filteredCards = topRowCards.filter(card => card.rolesAllowed && card.rolesAllowed.some(role => userRoles.includes(role)));

  // Memoize filteredCards to prevent filtering on every render
  const filteredCards = useMemo(() => 
    topRowCards.filter(
      (card) => card.visible !== false && card.rolesAllowed.some((role) => userRoles.includes(role))
    ),
    [topRowCards, userRoles]
  );
  return (
    <Layout className="min-h-screen bg-white font-sans">
      {/* sider start */}

      {/* <Sider
        width={80}
        style={{
          background: '#FAFAFA',
          height: '300px',
        }}
      >
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          paddingTop: '16px',
          gap: '32px',
          borderRadius: '8px',
        }}>
          {widgets.map((widget, index) => (
            <Tooltip
              key={index}
              title={widget.tooltip}
              placement="right"
            >
              <div
                style={{
                  width: '48px',
                  height: '48px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '14px',
                  border: '3px solid rgba(134, 41, 164, 0.5)',
                  background: 'rgba(234, 225, 233, 0.5)',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgb(218, 194, 224)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(234, 225, 233, 0.5)';
                }}
              >
                {widget.icon}
              </div>
            </Tooltip>
          ))}
        </div>
      </Sider> */}

      {/* sider end */}

      {/* Main Content */}
      <Content className="p-4 md:p-4">
        <div className="max-w-[1200px] mx-auto">
          <div className="flex flex-col space-y-3">
            {/* Top Row Cards */}
            {!rolesLoaded ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                <Spin size="large" tip="Loading your dashboard..." />
              </div>
            ) : (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, 250px)',
                  gap: '24px',
                  justifyContent: 'center',
                  paddingBottom: '12px',
                }}
              >
                {filteredCards.map((card, index) => (
                <div
                  key={index}
                  style={{
                    cursor: 'pointer',
                    position: 'relative',
                    opacity: '1',
                    transition: 'opacity 0.3s ease',
                  }}
                >
                  <ActionCard
                    icon={card.icon}
                    title={card.title}
                    message={card.message || ''}
                    background={card.background}
                    border={card.border}
                    variant="default"
                    onClick={card.onClick}
                  />
                </div>
              ))}
              </div>
            )}
          </div>
        </div>
      </Content>
      <Celebrate
        visible={isCelebrateModalVisible}
        onClose={handleCelebrateModalClose}
      />
      <Appreciate
        visible={isAppreciateModalVisible}
        onClose={handleAppreciateModalClose}
      />
      <Rewards
        visible={isRewaredsModalVisible}
        onClose={handleRewardsModalClose}
        userRoles={userRoles}
      />
      <MyPoints
        visible={isPointsModalVisible}
        onClose={handlePointsModalClose}
      />
      {/* <ApprovalModal /> */}
    </Layout>
  );
});

export default AppLayoutContent;


